/**
 * @file brand.ts
 * @description Vocabulário canônico de "marca" (tenant) do ecossistema.
 *
 * A Eva deixou de ser a ausência de parceiro e passou a ser uma marca como as
 * outras, com documento próprio em `partners` (id `default`). Este arquivo é a
 * fonte única de como se pergunta "de quem é isto?" no sistema inteiro.
 *
 * ## Por que este arquivo existe
 *
 * Antes da migração havia SEIS formas de dizer "sem parceiro / é da Eva":
 *
 *   - `null`, `undefined` ou `''`      — na maioria dos documentos
 *   - `'default'`  (`EVA_TENANT_ID`)   — no agendamento, que já tratava Eva como tenant
 *   - `'__eva_global__'` (`GLOBAL_SCOPE`) — na whitelist de CPF
 *   - `'__none__'` (`NO_PARTNER_KEY`)  — no filtro de engajamento
 *   - `'eva'` (`EVA_FEED_ID`)          — no feed da comunidade no mobile
 *   - ids de singleton (`default`, `global`) — nas coleções globais
 *
 * Cada uma respondia à mesma pergunta com uma grafia diferente, e a checagem
 * correspondente (`if (!partnerId)`) estava espalhada por ~200 pontos. Trocar o
 * dado sem antes centralizar a pergunta inverteria o valor-verdade dessas
 * checagens de forma silenciosa — e checagem de tenancy que falha em silêncio é
 * vazamento de dado entre marcas.
 *
 * Por isso os helpers daqui aceitam TODAS as grafias e respondem igual ao código
 * anterior. Isso permite migrar os call sites primeiro (sem mudança de
 * comportamento) e o dado depois. Ver `docs/ecossistema-de-marcas/DECISOES.md`,
 * decisão D003.
 */

import { z } from 'zod';

/**
 * Identificador da Eva como marca.
 *
 * O valor `'default'` não foi escolhido agora: é o id que o `globalAppConfig`
 * já usava e que o agendamento já adotara como `EVA_TENANT_ID`. Reusá-lo faz a
 * migração ser um alargamento do que já existia, e não uma terceira convenção.
 */
export const EVA_BRAND_ID = 'default';

/**
 * Grafias legadas que significavam "Eva / sem parceiro" antes da migração.
 *
 * Continuam sendo aceitas na LEITURA enquanto houver documento não migrado.
 * Nunca use nenhuma delas para gravar — grave sempre `EVA_BRAND_ID`.
 */
const GRAFIAS_LEGADAS_DA_EVA: readonly string[] = [
  '',
  '__eva_global__', // GLOBAL_SCOPE, whitelist de CPF
  '__none__',       // NO_PARTNER_KEY, filtro de engajamento
  'eva',            // EVA_FEED_ID, feed do mobile
  'global',         // id dos singletons globalBarriers / globalAccessControl
] as const;

/**
 * Tudo o que um campo de marca pode conter fisicamente e significar "Eva":
 * a forma canônica, o null explícito e as grafias legadas. É a lista de quem
 * precisa alcançar a Eva por consultas de igualdade, quando a leitura ampla
 * não é possível (`findByBrand`, plano B). Campo AUSENTE não está aqui porque
 * nenhuma consulta de igualdade o alcança.
 */
export const GRAFIAS_FISICAS_DA_EVA: readonly (string | null)[] = [null, EVA_BRAND_ID, ...GRAFIAS_LEGADAS_DA_EVA];

/**
 * Referência a uma marca como vem do banco, antes de normalizar.
 *
 * O `''` está aqui porque o `ExternalUserSchema` sempre teve um transform que
 * mapeava string vazia para null — havia documento gravado assim.
 */
export type BrandRef = string | null | undefined;

/**
 * Normaliza qualquer grafia de marca para a forma canônica.
 *
 * Toda ausência e toda grafia legada da Eva viram `EVA_BRAND_ID`; qualquer
 * outro valor passa intacto. É a função que torna possível ler documento
 * migrado e não migrado com o mesmo código.
 */
export function normalizeBrandId(ref: BrandRef): string {
  if (ref === null || ref === undefined) return EVA_BRAND_ID;
  const trimmed = ref.trim();
  if (trimmed === '') return EVA_BRAND_ID;
  if (GRAFIAS_LEGADAS_DA_EVA.includes(trimmed)) return EVA_BRAND_ID;
  return trimmed;
}

/**
 * A referência aponta para a marca Eva?
 *
 * Substitui o antigo `!partnerId`. A diferença crucial: `!partnerId` responde
 * "não tem dono", que era uma pergunta sobre o dado; esta responde "o dono é a
 * Eva", que é uma pergunta sobre o domínio — e continua verdadeira depois que o
 * documento passa a ter `partnerId: 'default'` gravado.
 */
export function isEvaBrand(ref: BrandRef): boolean {
  return normalizeBrandId(ref) === EVA_BRAND_ID;
}

/**
 * Duas referências apontam para a mesma marca?
 *
 * Compara normalizando os dois lados, então documento migrado e não migrado
 * comparam iguais durante a janela de migração.
 */
export function sameBrand(a: BrandRef, b: BrandRef): boolean {
  return normalizeBrandId(a) === normalizeBrandId(b);
}

/**
 * Campo `partnerId` na forma canônica, para schemas de documento.
 *
 * Normaliza na leitura: documento gravado sem o campo, com `null` ou com grafia
 * legada passa a ler como `'default'`. É o passo "expand" — nenhum documento
 * precisa ser reescrito para o código novo funcionar.
 *
 * Use `brandIdField()` em coleções onde todo documento tem dono (o normal).
 */
export const brandIdField = () =>
  z.preprocess((raw) => normalizeBrandId(raw as BrandRef), z.string().min(1));

/**
 * Identificador da PORTA BASE — o endereço sem subdomínio (A044).
 *
 * Não é uma marca: é "a porta do ecossistema". Qual marca ela mostra é
 * configuração do mantenedor (`screenTemplates/default.marcaDaPortaBase`);
 * o servidor resolve `base` para essa marca. Cliente e e-mail usam este
 * valor onde antes usavam `EVA_BRAND_ID` para dizer "sem slug".
 */
export const PORTA_BASE = 'base';

/**
 * A marca de um vínculo, com a exceção do mantenedor (A044).
 *
 * `MAINTAINER` governa o ecossistema e não pertence a marca nenhuma: o campo
 * é `null`. Documento antigo gravado com `'default'` lê como `null` — é o
 * expand; a migração `mantenedor-sem-marca` regrava por higiene. Todo outro
 * papel tem marca, normalizada como sempre.
 */
export function marcaDoVinculo(role: unknown, raw: unknown): string | null {
  if (role === 'MAINTAINER') return null;
  return normalizeBrandId(raw as BrandRef);
}

/**
 * Igual a `brandIdField`, mas preserva `null` como valor distinto de Eva.
 *
 * Existe para o punhado de casos em que "sem marca" é semanticamente diferente
 * de "da Eva" — por exemplo um filtro de busca que quer dizer "qualquer marca".
 * Se você está em dúvida entre os dois, é `brandIdField`.
 */
export const optionalBrandIdField = () =>
  z.preprocess(
    (raw) => (raw === null || raw === undefined ? raw : normalizeBrandId(raw as BrandRef)),
    z.string().min(1).nullable().optional()
  );
